/**
 * Routing between two points on the map.
 *
 * Points, not places. There is no search box and no geocoder — you pick a
 * start and an end on the map and get a line. That is a deliberate limit: a
 * place search means a provider, a licence and a per-request bill, and the
 * question this answers is "how do I get from here to there", where here and
 * there are already on screen.
 *
 * Three modes, matching what the router is actually good at.
 */

import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";

import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

const MODES = [
  { key: "drive", label: "Drive" },
  { key: "walk", label: "Walk" },
  { key: "bike", label: "Bike" },
];

/**
 * Valhalla returns an encoded polyline at precision 6, not the 5 that most
 * decoders assume. Getting that wrong puts the route in the wrong hemisphere
 * rather than slightly off, which at least fails loudly.
 */
export function decodePolyline(encoded, precision = 6) {
  if (!encoded) return [];
  const factor = 10 ** precision;
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    points.push([lng / factor, lat / factor]);
  }
  return points;
}

/** "12 min" / "1 h 24 min" — nobody wants seconds. */
function formatDuration(seconds) {
  const total = Math.round((seconds ?? 0) / 60);
  if (total < 60) return `${total} min`;
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, "0")} min`;
}

function formatDistance(km) {
  const miles = (km ?? 0) * 0.621371;
  return miles < 0.2 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(1)} mi`;
}

export function createRouting({ apiBase = "./api", onChange, locate } = {}) {
  const state = {
    mode: "drive",
    // The start is always where you are. Routing from an arbitrary point to
    // another arbitrary point is a planning tool; routing from *here* is the
    // question someone standing in the city actually has.
    from: null,
    to: null,
    path: [],
    summary: null,
    error: null,
    picking: null,   // "from" | "to" | null
    arrived: false,
    busy: false,
  };

  async function solve() {
    if (!state.from || !state.to) {
      state.path = [];
      state.summary = null;
      return;
    }
    state.busy = true;
    state.error = null;
    onChange?.();

    const query = new URLSearchParams({
      from_lng: state.from[0], from_lat: state.from[1],
      to_lng: state.to[0], to_lat: state.to[1],
      mode: state.mode,
    });

    try {
      const response = await fetch(`${apiBase}/route?${query}`);
      const body = await response.json();
      if (!response.ok) {
        // 503 means the router is still building its tiles, which on a first
        // run is a wait rather than a fault. Say which.
        state.error = response.status === 503
          ? "Router still starting up — tiles are being built."
          : (body.error ?? `Could not route (${response.status})`);
        state.path = [];
        state.summary = null;
      } else {
        state.path = (body.shapes ?? []).flatMap((s) => decodePolyline(s));
        state.summary = {
          distance: formatDistance(body.distance_km),
          duration: formatDuration(body.duration_s),
          steps: body.maneuvers?.length ?? 0,
        };
      }
    } catch (error) {
      state.error = error.message;
      state.path = [];
    } finally {
      state.busy = false;
      onChange?.();
    }
  }

  return {
    state,
    modes: MODES,

    setMode(mode) {
      state.mode = mode;
      return solve();
    },

    /** Arm the next map click to set the destination. */
    pick() {
      state.picking = state.picking ? null : "to";
      onChange?.();
    },

    /** Consumed by the map click handler; returns true if it took the click. */
    placeAt(lngLat) {
      if (!state.picking) return false;
      state.to = [lngLat.lng, lngLat.lat];
      state.picking = null;
      state.arrived = false;
      // Read the location fresh at the moment of routing: someone who moved
      // since the page loaded should be routed from where they are now.
      state.from = locate?.() ?? state.from;
      if (!state.from) {
        state.error = "Your location is not available — allow it to route from here.";
        onChange?.();
        return true;
      }
      solve();
      return true;
    },

    /**
     * Arrived. The line stays, the prompt changes.
     *
     * Deliberately not clear(): erasing the route the moment somebody reaches
     * the end takes away the one thing that shows where they just walked, at
     * exactly the moment they might look up to check they are in the right
     * place.
     */
    arrive() {
      state.arrived = true;
      onChange?.();
    },

    clear() {
      state.to = null;
      state.path = [];
      state.summary = null;
      state.error = null;
      state.picking = null;
      onChange?.();
    },

    /** deck.gl layers for the current route. */
    layers() {
      const out = [];
      const c = palette();

      if (state.path.length > 1) {
        // Drawn twice: a dark casing under a bright line, so the route stays
        // legible over both a near-black ground and a pale one.
        out.push(new PathLayer({
          id: "route-casing",
          data: [{ path: state.path }],
          getPath: (d) => d.path,
          getColor: [0, 0, 0, 170],
          getWidth: 11,
          widthUnits: "pixels",
          capRounded: true,
          jointRounded: true,
          pickable: false,
          parameters: OVER_EVERYTHING,
        }));
        out.push(new PathLayer({
          id: "route-line",
          data: [{ path: state.path }],
          getPath: (d) => d.path,
          getColor: [...c.feed, 245],
          getWidth: 6,
          widthUnits: "pixels",
          capRounded: true,
          jointRounded: true,
          pickable: false,
          parameters: OVER_EVERYTHING,
        }));
      }

      const ends = [
        state.from && { p: state.from, end: "from" },
        state.to && { p: state.to, end: "to" },
      ].filter(Boolean);

      if (ends.length) {
        out.push(new ScatterplotLayer({
          id: "route-ends",
          data: ends,
          getPosition: (d) => d.p,
          getRadius: 9,
          radiusUnits: "pixels",
          stroked: true,
          filled: true,
          getLineWidth: 2,
          lineWidthUnits: "pixels",
          getFillColor: (d) => (d.end === "from" ? [...c.feed, 235] : [255, 255, 255, 235]),
          updateTriggers: { getFillColor: themeName() },
          getLineColor: [0, 0, 0, 220],
          pickable: false,
          parameters: OVER_EVERYTHING,
        }));
      }
      return out;
    },
  };
}
